import { IconChevronRight16 } from '@warp-ds/icons/react';
import cn from 'classnames';

import { memo, type ReactNode, useCallback } from 'react';

import { replaceDashWithComma } from '../../../../util/formatting';

import type { AutocompleteOption, AutocompleteProps } from '../../../types';

import styles from '../Autocomplete.module.css';

interface AutocompleteOptionItemProps {
    option: AutocompleteOption;
    index: number;
    id: string;
    selected: number;
    vertical: AutocompleteProps['vertical'];
    departure?: boolean;
    onSelect: (option: AutocompleteOption) => void;
    onHover: (index: number | null) => void;
    icon: ReactNode | null;
    defaultView?: boolean;
    shouldShowCount?: boolean;
}

export const AutocompleteOptionItem = memo(
    ({
        option,
        index,
        id,
        selected,
        vertical,
        onSelect,
        onHover,
        icon,
        defaultView = false,
        shouldShowCount = true,
    }: AutocompleteOptionItemProps) => {
        const handleClick = useCallback(() => onSelect(option), [option, onSelect]);
        const handleHover = useCallback(() => onHover(index), [index, onHover]);

        return (
            <li
                id={`${id}-option-${index}`}
                // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: <li> is given role="option" as required by the ARIA listbox pattern
                role="option"
                aria-selected={selected === index}
                onClick={handleClick}
                onMouseOver={handleHover}
                tabIndex={-1}
                className={cn(styles['autocomplete-option-item'], 'flex flex-row items-center cursor-pointer gap-8', {
                    's-bg-primary-subtle': selected === index,
                    'flex-row justify-between': vertical === 'fhh',
                })}>
                {icon}
                <span title={option.text} className={styles['autocomplete-option-item__text']}>
                    {replaceDashWithComma(option.text)}
                </span>
                {shouldShowCount && option.count && <span className="font-bold">{option.count}</span>}
                {defaultView && <IconChevronRight16 aria-hidden focusable="false" />}
            </li>
        );
    },
);

AutocompleteOptionItem.displayName = 'AutocompleteOptionItem';
